// src/views/add/Add1.js

import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import { et } from "date-fns/locale";
import { setDefaultOptions } from "date-fns";
import { FaTimesCircle } from "react-icons/fa";
import EstateAutocomplete from "../../components/EstateAutocompete";
import CameraComponent from "../../components/camera";
import QRScanner from "../../components/QRScanner";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useNotification } from "../../context/NotificationContext";
import { normalizeDecimal, isValidDecimal, formatDecimal } from "../../utils/formatters";
import {
  fetchMeterBrands,
  fetchQnoms,
  fetchDiameters,
  createMeterReport,
  updateMeterReport,
  uploadMeterReportImage,
  fetchMeterReport,
  fetchMetersByEstate,
  checkMeterExists,
} from "../../data/fetchData";
import "../../styles/CustomCalendar.css";

registerLocale("et", et);
setDefaultOptions({ locale: et });

const TYPE_OPTIONS = [
  { value: "MAIN_GAUGE", label: "Põhiarvesti" },
  { value: "SUB_GAUGE", label: "Alamarvesti" },
  { value: "WATERING", label: "Kastmine" },
];

export default function Add1() {
  const [id, setId] = useState(null);
  const [estate, setEstate] = useState(null);
  const [estateOption, setEstateOption] = useState(null);
  const [aadress, setAadress] = useState("");
  const [omanik, setOmanik] = useState(""); // Displays agreement partner name
  const [tehaseNumber, setTehaseNumber] = useState("");
  const [arvestiNait, setArvestiNait] = useState("");
  const [workDate, setWorkDate] = useState(new Date());
  const [viimaneTaatlemine, setViimaneTaatlemine] = useState(new Date());
  const [sealDate, setSealDate] = useState(new Date());
  const [sealNumber, setSealNumber] = useState("");
  const [meterIsOnConsole, setConsole] = useState("Jah");
  const [veemootjaMark, setVeemootjaMark] = useState("");
  const [qn, setQn] = useState("");
  const [diameter, setDiameter] = useState("");
  const [meterType, setMeterType] = useState("");
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [meterNodeCompliant, setMeterNodeCompliant] = useState("");

  const [meters, setMeters] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);
  const [diameters, setDiameters] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeNumber } = useParams();
  const { showError, showSuccess } = useNotification();

  // helper: base64 → Blob
  function dataURLtoBlob(dataURL) {
    const [header, base64] = dataURL.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  // Handle QR code scan for meter number
  const handleQRScan = (scannedData) => {
    // Extract meter number from scanned data
    // Assuming QR code contains just the meter number or needs some parsing
    let meterNumber = scannedData.toString().trim();
    
    // If QR code contains additional data, extract meter number
    // This may need adjustment based on actual QR code format
    if (meterNumber.includes(':')) {
      const parts = meterNumber.split(':');
      meterNumber = parts[parts.length - 1].trim();
    }
    
    // Allow alphanumeric meter identifiers
    if (/^[A-Za-z0-9]+$/.test(meterNumber)) {
      setTehaseNumber(meterNumber);
      showSuccess(`QR-koodist loetud arvesti number: ${meterNumber}`);
    } else {
      showError('QR-koodist ei õnnestunud kehtivat arvesti numbrit lugeda');
    }
  };

  // Fetch lookup data once
  useEffect(() => {
    fetchMeterBrands()
      .then(setMeters)
      .catch(() => showError("Markide laadimine nurjus"));
    fetchQnoms()
      .then(setQnOptions)
      .catch(() => showError("Qn laadimine nurjus"));
    fetchDiameters()
      .then(setDiameters)
      .catch(() => showError("Läbimõõtude laadimine nurjus"));
  }, []);

  // Load URL parameters for estate data
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const estateId = searchParams.get("estateId");
    const address = searchParams.get("address");
    const owner = searchParams.get("owner"); // legacy param name from backend
    const clientId = searchParams.get("clientId");

    if (estateId && address && owner) {

      const cid = clientId ? parseInt(clientId) : null;
      const estateObj = {
        id: parseInt(estateId),
        address: address,
        // Backwards compatible: keep owner fields, but prefer agreement_partner
        owner: cid,
        owner_name: owner,
        agreement_partner: cid,
        agreement_partner_name: owner,
      };

      setEstate(estateObj);
      setEstateOption({
        label: address,
        value: parseInt(estateId),
        estate: estateObj,
      });
      setAadress(address);
      setOmanik(owner);
    }
  }, [location.search]);

  // Only load existing work when editing (location.state.work)
  useEffect(() => {
    const populate = async (work) => {
      setId(work.number);
      const estateObj = {
        id: work.estate,
        address: work.estate_address,
        owner: work.client,
        owner_name: work.client_name,
        agreement_partner: work.client,
        agreement_partner_name: work.client_name,
      };
      setEstate(estateObj);
      setEstateOption({
        label: work.estate_address,
        value: work.estate,
        estate: estateObj,
      });
      setAadress(work.estate_address);
      setOmanik(work.client_name);
      setTehaseNumber(work.meter_number);
      const readingVal = work.reading != null ? Number(work.reading).toString() : "";
      setArvestiNait(readingVal);
      setWorkDate(new Date(work.date));
      setViimaneTaatlemine(new Date(work.last_certification_date));
      setSealDate(new Date(work.verification_date));
      setSealNumber(work.seal_number);
      setConsole(work.on_console ? "Jah" : "Ei");
      setVeemootjaMark(work.meter_brand?.toString() ?? "");
      setQn(work.q_nom?.toString() ?? "");
      setDiameter(work.diameter?.toString() ?? "");
      setMeterType(work.type);
      setNotes(work.notes || "");
      setMeterNodeCompliant(
        work.meter_node_compliant === true
          ? "Jah"
          : work.meter_node_compliant === false
          ? "Ei"
          : ""
      );
      // Handle images from backend - convert using proxy endpoint
      const imagePromises = (work.images || []).map(async (img) => {
        if (img.data) {
          // Already base64 data
          return img.data;
        } else if (img.id) {
          // Use backend proxy to avoid CORS issues
          try {
            const token = localStorage.getItem("apiToken");
            const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/meter-report-images/${img.id}/proxy/?token=${token}`;
            const response = await fetch(proxyUrl);
            if (response.ok) {
              const blob = await response.blob();
              // Use blob URL instead of data URL so we can detect existing images (not to re-upload)
              return URL.createObjectURL(blob);
            }
          } catch (error) {
            console.warn("Failed to load image via proxy:", img.id, error);
          }
        }
        return null;
      });
      
      Promise.all(imagePromises).then((images) => {
        setImages(images.filter(img => img !== null));
      });
    };

    // Skip if URL params are present (we loaded from URL already)
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("estateId")) {
      return;
    }

    if (location.state?.work) {
      populate(location.state.work);
    } else if (routeNumber) {
      fetchMeterReport(routeNumber)
        .then(populate)
        .catch(() => showError("Rapordi laadimine nurjus"));
    }
  }, [location.state, routeNumber]);

  // Auto-fill owner/address when estate changes
  useEffect(() => {
    if (!estate) return;
    setAadress(estate.address);
    setOmanik(estate.agreement_partner_name || estate.owner_name || "");
  }, [estate]);

  // Load draft data from localStorage if available (after other data loading)
  useEffect(() => {
    // Skip if we already have URL params or editing existing work
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("estateId") || location.state?.work || routeNumber) {
      return;
    }

    // Load draft from localStorage
    const draft = localStorage.getItem("addData");
    if (draft) {
      try {
        const data = JSON.parse(draft);

        // Restore form fields
        setId(data.id || "");
        setAadress(data.aadress || "");
        setOmanik(data.client_name || "");
        setTehaseNumber(data.tehaseNumber || "");
        setArvestiNait(data.arvestiNait || "");

        // Restore dates
        if (data.workDate) setWorkDate(new Date(data.workDate));
        if (data.viimaneTaatlemine)
          setViimaneTaatlemine(new Date(data.viimaneTaatlemine));
        if (data.sealDate) setSealDate(new Date(data.sealDate));

        setSealNumber(data.sealNumber || "");
        setConsole(data.meterIsOnConsole || "");
        setVeemootjaMark(data.veemootjaMark || "");
        setQn(data.qn || "");
        setDiameter(data.diameter || "");
        setMeterType(data.meterType || "");
        setNotes(data.notes || "");
        setMeterNodeCompliant(data.meterNodeCompliant || "");
        setImages(data.images || []);

        // Restore estate and estateOption if available
        if (data.estate && data.aadress) {
          const estateObj = {
            id: data.estate,
            address: data.aadress,
            owner: data.client,
            owner_name: data.client_name,
          };
          setEstate(estateObj);
          setEstateOption({
            label: data.aadress,
            value: data.estate,
            estate: estateObj,
          });
        }
      } catch (error) {
        console.error("Error loading draft data:", error);
      }
    }
  }, [location.search, location.state, routeNumber]); // Dependencies to ensure this runs after other loaders

  // Validation
  const isValidMeterNumber = (value) =>
    typeof value === "string" && /^[A-Za-z0-9]+$/.test(value.trim());

  // Validate meter readings with up to 3 decimal places (supports comma and dot)
  const isValidReading = (v) => {
    return isValidDecimal(v, 3);
  };

  const checkMainGaugeExists = async () => {
    if (meterType !== "SUB_GAUGE" && meterType !== "WATERING") {
      return true; // Only check for sub gauges and watering meters
    }

    if (!estate || !estate.id) {
      return true; // Let the regular validation catch this
    }

    try {
      const estateMeters = await fetchMetersByEstate(estate.id);
      const hasMainGauge = estateMeters.some(
        (meter) =>
          meter.type === "MAIN_GAUGE" &&
          (meter.status === "ACTIVE" || meter.status === "CLOSED")
      );

      if (!hasMainGauge) {
        const meterTypeName =
          meterType === "SUB_GAUGE" ? "Alamarvestit" : "Kastmisarvestit";
        showError(
          `${meterTypeName} ei saa lisada kinnistule, kus pole peaarvestit. Palun lisage esmalt peaarvesti.`
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking main gauge:", error);
      showError("Peaarvesti kontrollimisel tekkis viga.");
      return false;
    }
  };

  // Validation helpers
  // 1) Minimal validation for moving to next step (Edasi)
  const validateForNext = async () => {
    // Required: estate with owner
    if (!estate || !estate.id) {
      showError("Kinnistu peab olema valitud");
      return false;
    }

    if (!((estate.agreement_partner_name || estate.owner_name) && (estate.agreement_partner_name || estate.owner_name).trim() !== "")) {
      showError("Kinnistul puudub lepingupartner. Palun valige kinnistu, millel on lepingupartner määratud");
      return false;
    }

    // Required: number, mark, diameter, meter reading, seal number
    if (!tehaseNumber) {
      showError("Arvesti number peab olema täidetud");
      return false;
    }

    if (!isValidMeterNumber(tehaseNumber)) {
      showError("Arvesti number võib sisaldada ainult tähti ja numbreid");
      return false;
    }

    if (!veemootjaMark) {
      showError("Arvesti mark peab olema valitud");
      return false;
    }

    if (!diameter) {
      showError("Läbimõõt peab olema valitud");
      return false;
    }

    if (!isValidReading(arvestiNait)) {
      showError(
        "Arvesti näit peab olema kehtiv number (kuni 3 kohta peale koma)"
      );
      return false;
    }

    if (!sealNumber) {
      showError("Plommi number peab olema täidetud");
      return false;
    }

    // Check main gauge exists for sub gauges
    const mainGaugeCheck = await checkMainGaugeExists();
    if (!mainGaugeCheck) {
      return false;
    }

    // Check for duplicate meter number within the estate
    if (estate && estate.id) {
      const isDuplicate = await checkMeterExists(tehaseNumber, estate.id);
      if (isDuplicate) {
        showError(`Arvesti number ${tehaseNumber} on juba kinnistusel ${estate.address} kasutusel.`);
        return false;
      }
    }

    return true;
  };

  // 2) Validation for saving draft (Salvesta) – estate mandatory, check duplicates if meter number provided
  const validateForSave = async () => {
    if (!estate || !estate.id) {
      showError("Kinnistu peab olema valitud");
      return false;
    }

    const readingValue = (arvestiNait ?? "").toString().trim();
    if (readingValue && !isValidReading(readingValue)) {
      showError("Arvesti näit peab olema kehtiv number (kuni 3 kohta peale koma)");
      return false;
    }

    // Check for duplicate meter number if provided
    if (tehaseNumber && tehaseNumber.trim() !== "") {
      if (!/^\d+$/.test(tehaseNumber)) {
        showError("Arvesti number peab sisaldama ainult numbreid");
        return false;
      }

      const isDuplicate = await checkMeterExists(tehaseNumber, estate.id);
      if (isDuplicate) {
        showError(`Arvesti number ${tehaseNumber} on juba kinnistusel ${estate.address} kasutusel.`);
        return false;
      }
    }

    return true;
  };

  // Save to backend (create or update) + image upload
  const saveProgress = async (e) => {
    e.preventDefault();
    if (!(await validateForSave())) return;

    // NOTE: Other fields are optional when saving draft – convert empty strings to null
    const readingValue = (arvestiNait ?? "").toString().trim();
    const readingProvided = readingValue !== "";

    const payload = {
      action: "add",
      estate: estate?.id,
      client: estate?.agreement_partner ?? estate?.owner,
      meter_number: tehaseNumber || null,
      meter_brand: veemootjaMark ? Number(veemootjaMark) : null,
      q_nom: qn ? Number(qn) : null,
      type: meterType || null,
      diameter: diameter ? Number(diameter) : null,
      last_certification_date: viimaneTaatlemine.toISOString().slice(0, 10),
      verification_date: sealDate.toISOString().slice(0, 10),
      seal_number: sealNumber || null,
      sealing_date: sealDate.toISOString().slice(0, 10),
      meter_node_compliant:
        meterNodeCompliant === "Jah"
          ? true
          : meterNodeCompliant === "Ei"
          ? false
          : null,
      on_console: meterIsOnConsole === "Jah",
      date: new Date().toISOString(), // Always use today's date to ensure it's visible in backend
      status: "DRAFT",
      notes:
        workDate.toDateString() !== new Date().toDateString()
          ? `Tegelik paigaldamise kuupäev: ${workDate.toLocaleDateString(
              "et-EE"
            )}${notes ? "\n" + notes : ""}`
          : notes || null,
    };
    if (readingProvided) {
      payload.reading = Number(normalizeDecimal(readingValue));
    }

    try {
      const saved = id
        ? await updateMeterReport(id, payload)
        : await createMeterReport(payload);

      if (!saved || !saved.id) {
        showError("Backend ei tagastanud kehtivat vastust");
        return;
      }

      showSuccess(
        id ? "Aruanne uuendatud edukalt." : "Aruanne salvestatud edukalt."
      );

      const reportPk = saved.id;
      let uploadIndex = 1;
      for (const src of images) {
        if (!src.startsWith("data:")) {
          // existing image (fetched for preview), skip it
          continue;
        }
        const blob = dataURLtoBlob(src);
        const filename = `report-${saved.number}-${uploadIndex}.png`;
        await uploadMeterReportImage(reportPk, blob, filename);
        uploadIndex++;
      }

      localStorage.removeItem("addData");
      navigate("/works");
    } catch (err) {
      console.error("Error saving meter report:", err);

      // Try to extract meaningful error messages from backend response
      let detail = err.response?.data?.detail;
      if (!detail && err.response?.data && typeof err.response.data === "object") {
        // Collect field-specific errors
        const parts = Object.entries(err.response.data).map(([field, msgs]) => {
          if (Array.isArray(msgs)) return `${field}: ${msgs.join(", ")}`;
          if (typeof msgs === "string") return `${field}: ${msgs}`;
          return `${field}: viga`;
        });
        if (parts.length) detail = parts.join("; ");
      }

      showError(`Salvestamisel viga: ${detail || err.message || "Tundmatu viga"}`);
    }
  };

  // Save draft & go to step 2
  const handleNext = async () => {
    if (!(await validateForNext())) return;
    const draft = {
      id,
      aadress,
      client_name: omanik,
      client: estate?.agreement_partner ?? estate?.owner,
      estate: estate?.id,
      tehaseNumber,
      arvestiNait,
      workDate,
      viimaneTaatlemine,
      sealDate,
      sealNumber,
      meterIsOnConsole,
      veemootjaMark,
      qn,
      diameter,
      meterType,
      notes,
      meterNodeCompliant,
      images,
      workType: "Paigaldamine",
    };
    localStorage.setItem("addData", JSON.stringify(draft));
    navigate("/Add2");
  };

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border-gray-300 border">
      <header className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Veemõõtja paigaldamine</h1>
      </header>

      <form onSubmit={saveProgress} className="space-y-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column */}
          <div>
            <div className="mb-4">
              <label className="block mb-2 text-secondary">Kinnistu</label>
              <EstateAutocomplete
                value={estateOption}
                onEstateChange={(opt) => {
                  setEstate(opt?.estate || null);
                  setEstateOption(opt);
                  // Update agreement partner name from backend data
                  setOmanik(opt?.estate?.agreement_partner_name || opt?.estate?.owner_name || "");
                  // Also update address for display consistency
                  setAadress(opt?.estate?.address || "");
                }}
                inputId="estate-autocomplete"
                placeholder="Alusta aadressi tippimist…"
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">Lepingupartner</label>
              <input
                className="w-full p-3 border border-gray-300 rounded-md bg-gray-100"
                value={omanik}
                readOnly
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">
                Arvesti number
              </label>
              <input
                className="w-full p-3 border border-gray-300 rounded-md"
                value={tehaseNumber}
                onChange={(e) => setTehaseNumber(e.target.value)}
                placeholder="Sisesta arvesti number või skaneeri QR-kood"
              />
              <div className="mt-2">
                <QRScanner 
                  onScan={handleQRScan}
                  placeholder="Skaneeri arvesti QR-kood"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">Arvesti näit</label>
              <input
                className="w-full p-3 border border-gray-300 rounded-md"
                value={arvestiNait}
                onChange={(e) => setArvestiNait(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">
                Paigaldamise kuupäev
              </label>
              <DatePicker
                selected={workDate}
                onChange={setWorkDate}
                dateFormat="dd-MM-yyyy"
                locale="et"
                className="w-full p-3 border border-gray-300 rounded-md"
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">Vali tüüp</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-md"
                value={meterType}
                onChange={(e) => setMeterType(e.target.value)}
              >
                <option value="" disabled>
                  Vali siit
                </option>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">Plommi number</label>
              <input
                className="w-full p-3 border border-gray-300 rounded-md"
                value={sealNumber}
                onChange={(e) => setSealNumber(e.target.value)}
              />
            </div>
          </div>

          {/* Right column */}
          <div>
            <div className="mb-4">
              <label className="block mb-2 text-secondary">Arvesti mark</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-md"
                value={veemootjaMark}
                onChange={(e) => setVeemootjaMark(e.target.value)}
              >
                <option value="" disabled>
                  Vali mark
                </option>
                {meters.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">Vali Qn/m³</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-md"
                value={qn}
                onChange={(e) => setQn(e.target.value)}
              >
                <option value="" disabled>
                  Vali siit
                </option>
                {qnOptions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.q_nom}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">Vali Läbimõõt</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-md"
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
              >
                <option value="" disabled>
                  Vali siit
                </option>
                {diameters.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.diameter}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">
                Viimane taatlemine
              </label>
              <DatePicker
                selected={viimaneTaatlemine}
                onChange={setViimaneTaatlemine}
                dateFormat="dd-MM-yyyy"
                locale="et"
                className="w-full p-3 border border-gray-300 rounded-md"
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">
                Plommimise kuupäev
              </label>
              <DatePicker
                selected={sealDate}
                onChange={setSealDate}
                dateFormat="dd-MM-yyyy"
                locale="et"
                className="w-full p-3 border border-gray-300 rounded-md"
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">
                Veemõõtja konsoolil
              </label>
              <select
                className="w-full p-3 border border-gray-300 rounded-md"
                value={meterIsOnConsole}
                onChange={(e) => setConsole(e.target.value)}
              >
                <option>Jah</option>
                <option>Ei</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">
                Veemõõdu sõlm vastab nõuetele
              </label>
              <select
                className="w-full p-3 border border-gray-300 rounded-md"
                value={meterNodeCompliant}
                onChange={(e) => setMeterNodeCompliant(e.target.value)}
              >
                <option value="" disabled>
                  Vali siit
                </option>
                <option>Jah</option>
                <option>Ei</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-secondary">Lisa pilt</label>
              <CameraComponent
                onCapture={(img) => setImages((prev) => [...prev, img])}
              />
            </div>
          </div>

          {/* Images preview */}
          <div className="col-span-full mb-4">
            {images.map((src, i) => (
              <div key={i} className="inline-block m-2 relative">
                <img
                  src={src}
                  alt={`#${i + 1}`}
                  className="w-24 h-24 object-cover rounded-md"
                />
                <button
                  onClick={() =>
                    setImages((prev) => prev.filter((_, j) => j !== i))
                  }
                  className="absolute top-0 right-0 text-red-500 bg-white rounded-full p-1"
                >
                  <FaTimesCircle size={20} />
                </button>
              </div>
            ))}
          </div>

          {/* Notes */}
          <div className="col-span-full">
            <label className="block mb-2 text-secondary">Märkused</label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              maxLength={4096}
              placeholder="Lisa märkused..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="bg-gray-300 px-6 py-3 rounded-md hover:bg-gray-400"
          >
            Tagasi
          </button>
          <div className="space-x-2">
            <button
              type="submit"
              className="bg-secondary px-6 py-3 rounded-md text-white hover:bg-secondary-hover"
            >
              Salvesta
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="bg-primary px-6 py-3 rounded-md text-white hover:bg-primary-hover"
            >
              Edasi
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
